/**
 * ═══════════════════════════════════════════════════════════════════════════
 * linkHubIrcService.ts - IRC Service Frontend
 * ═══════════════════════════════════════════════════════════════════════════
 * @version 1.1.0
 * @date 2025-01-19
 * 
 * Ajustado para conectar a backend en 10.10.5.1:2626
 * 
 * Changelog v1.1.0:
 * - ✅ IRCMessage interface incluye isPrivate flag
 * - ✅ Support para mensajes privados
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { getHatateToken } from '../helpers/utils';

export interface IRCConfig {
  server: string;
  port: number;
  nickname: string;
  channels?: string[];
  useSSL?: boolean;
  password?: string;
  alternativeNicks?: string[];
}

export interface IRCMessage {
  id?: string;
  type: string;
  channel?: string;
  from?: string;
  sender?: string;
  sender_host?: string;
  text?: string;
  message?: string;
  timestamp: number | string;
  isPrivate?: boolean;  // ✅ Flag para identificar mensajes privados
  metadata?: Record<string, any>;
}

export interface IRCChannel {
  name: string;
  users: number;
  user_list?: string[];
  topic?: string;
  mode?: string;
  message_count?: number;
  unread?: number;
}

export interface IRCUser {
  nick: string;
  host: string;
  privilege?: 'owner' | 'op' | 'voice' | 'user';
  idle?: number;
  channels?: string[];
}

export interface IRCStats {
  total_users: number;
  active_channels: number;
  message_rate: number;
  total_messages: number;
  websocket_clients: number;
  network_load: number;
  uptime: number;
  timestamp: string;
  active_connections: number;
}

export interface IRCConnectionStatus {
  connected: boolean;
  server: string;
  nickname: string;
  channels: string[];
  error?: string;
}


const API_KEY = getHatateToken();
class LinkHubIrcService {
  // ✅ CAMBIO: Apuntar al nuevo backend en 10.10.5.1:2626
  private readonly REST_API_URL = 'https://backend.anubisai.net/api';
  
  private ws: EventSource | WebSocket | null = null;
  private connected: boolean = false;
  private connectionId: string | null = null;
  private currentConfig: IRCConfig | null = null;
  private currentUserId: string | null = null;
  
  private apiKey: string = 'anubis-whatsapp-api-key';
  private authToken: string = API_KEY;
  
  private messageListeners: Set<(msg: IRCMessage) => void> = new Set();
  private eventListeners: Set<(event: IRCMessage) => void> = new Set();
  private errorListeners: Set<(error: IRCMessage) => void> = new Set();
  private statsListeners: Set<(stats: IRCStats) => void> = new Set();
  
  private channelsCache: IRCChannel[] = [];
  private usersCache: Map<string, IRCUser[]> = new Map();
  private statsCache: IRCStats | null = null;
  
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 2000;
  private reconnectTimer: NodeJS.Timeout | null = null;
  
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private heartbeatTimeout: number = 30000;

  constructor() {
    console.log('🎧 LinkHub IRC Service inicializado');
    this.initAuth();
  }

  private initAuth(): void {
    const token = localStorage.getItem('hatateToken');
    if (token) {
      this.authToken = `Bearer ${token}`;
      console.log('🔐 Bearer token cargado automáticamente');
    }
}

  setAuth(apiKey: string, bearerToken: string): void {
    this.apiKey = apiKey;
    this.authToken = bearerToken.startsWith('Bearer ') ? bearerToken : `Bearer ${bearerToken}`;
    console.log('🔐 Headers de autenticación configurados');
  }

  private getHeaders(): HeadersInit {
    // Auto-recargar token si está vacío
    if (!this.authToken) {
      this.initAuth();
    }
    
    return {
      'Content-Type': 'application/json',
      'x-api-key': this.apiKey,
      'Authorization': this.authToken
    };
}

  async connect(config: IRCConfig): Promise<string> {
    try {
      console.log('========================================');
      console.log('🔌 INICIANDO CONEXIÓN A IRC VÍA BACKEND');
      console.log('========================================');
      console.log('📋 Configuración:', config);
      console.log('🌐 REST API:', this.REST_API_URL);
      
      if (!this.authToken) {
        throw new Error('Headers de autenticación no configurados. Llama a setAuth() primero.' + this.authToken + 'test');
      }

      this.currentConfig = config;

      if (this.authToken) {
      try {
        const tokenParts = this.authToken.replace('Bearer ', '').split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          this.currentUserId = payload._id || payload.userId || payload.id || null;
          console.log(`🆔 UserId detectado: ${this.currentUserId}`);
        }
      } catch (e) {
        console.log(`⚠️ No se pudo extraer userId del token`);
      }
    }

      // Paso 1: Conectar al IRC vía backend
      console.log('📡 Paso 1: Conectando al IRC...');
      const response = await fetch(`${this.REST_API_URL}/linkhub/connect`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(config)
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Error conectando: ${response.status} - ${error}`);
      }

      const result = await response.json();
      console.log('✅ Respuesta connect:', result);

      // Paso 2: Conectar SSE para eventos
      console.log('🔌 Paso 2: Conectando SSE...');
      await this.connectWebSocket();

      this.connected = true;
      this.connectionId = result.connectionId || `conn_${Date.now()}`;
      this.reconnectAttempts = 0;

      console.log('========================================');
      console.log('✅ CONEXIÓN EXITOSA!');
      console.log(`🆔 Connection ID: ${this.connectionId}`);
      console.log('========================================');

      return this.connectionId;

    } catch (error: any) {
      console.error('========================================');
      console.error('❌ ERROR EN CONEXIÓN IRC');
      console.error('========================================');
      console.error('Error:', error.message || error);
      console.error('Stack:', error.stack);
      console.error('========================================');

      this.connected = false;
      this.notifyError({
        type: 'CONNECTION_ERROR',
        text: error.message || 'Error al conectar al IRC',
        timestamp: Date.now(),
        metadata: { error }
      });

      throw error;
    }
  }

  private connectWebSocket(): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      console.log('🔌 Conectando SSE stream...');
      
      const token = this.authToken.replace('Bearer ', '');
      const sseUrl = `${this.REST_API_URL}/linkhub/stream?token=${encodeURIComponent(token)}`;
      console.log('🌐 SSE URL:', sseUrl);
      
      // ✅ EventSource NO permite headers personalizados
      // Por eso el error - está enviando Content-Type por defecto
      const eventSource = new EventSource(sseUrl);

      const timeout = setTimeout(() => {
        eventSource.close();
        reject(new Error('SSE timeout (10s)'));
      }, 10000);

      eventSource.onopen = () => {
        clearTimeout(timeout);
        console.log('✅ SSE stream conectado');
        this.ws = eventSource as any;
        resolve();
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleWebSocketMessage(data);
        } catch (error) {
          console.error('Error parseando mensaje SSE:', error);
        }
      };

      eventSource.onerror = (error) => {
        clearTimeout(timeout);
        console.error('❌ SSE error:', error);
        
        if (eventSource.readyState === EventSource.CONNECTING) {
          console.log('🔄 SSE reconectando...');
        } else {
          eventSource.close();
          reject(error);
        }
      };

    } catch (error) {
      reject(error);
    }
  });
}

  private handleWebSocketMessage(data: any): void {
  const msgType = data.type || data.event;
  
  // ✅ LOG DETALLADO
  console.log('═══════════════════════════════════════════');
  console.log('📨 SSE MENSAJE RECIBIDO');
  console.log('═══════════════════════════════════════════');
  console.log('Tipo:', msgType);
  console.log('Data completa:', JSON.stringify(data, null, 2));
  console.log('═══════════════════════════════════════════');

  switch (msgType) {
    case 'STREAM_READY':
      console.log('✅ Stream listo');
      break;

    case 'CONNECTED':
      console.log('✅ IRC conectado:', data.text);
      this.notifyEvent({
        type: 'CONNECTED',
        text: data.text || 'Conectado a IRC',
        timestamp: Date.now(),
        metadata: data
      });
      break;

    case 'PRIVMSG':
      console.log('📩 MENSAJE IRC RECIBIDO');
      console.log('  Canal:', data.channel);
      console.log('  De:', data.from || data.sender);
      console.log('  Texto:', data.text);
      
      const message: IRCMessage = {
        id: data.id || `msg_${Date.now()}_${Math.random()}`,
        type: 'PRIVMSG',
        channel: data.channel,
        from: data.from || data.sender,
        sender: data.sender || data.from,
        text: data.text,
        timestamp: data.timestamp || Date.now(),
        metadata: data.metadata || {}
      };
      
      // ✅ CRÍTICO: Notificar a los listeners
      console.log('🔔 Notificando a listeners:', this.messageListeners.size, 'listeners');
      this.notifyMessage(message);
      break;

    case 'JOIN':
      console.log('📥 Usuario se unió:', data.from, '→', data.channel);
      this.notifyEvent({
        type: 'JOIN',
        channel: data.channel,
        from: data.from,
        text: data.text,
        timestamp: data.timestamp || Date.now(),
        metadata: data
      });
      break;

    case 'PART':
      console.log('📤 Usuario salió:', data.from, '←', data.channel);
      this.notifyEvent({
        type: 'PART',
        channel: data.channel,
        from: data.from,
        text: data.text,
        timestamp: data.timestamp || Date.now(),
        metadata: data
      });
      break;

    case 'QUIT':
      console.log('🚪 Usuario desconectó:', data.from);
      this.notifyEvent({
        type: 'QUIT',
        from: data.from,
        text: data.text,
        timestamp: data.timestamp || Date.now(),
        metadata: data
      });
      break;

    case 'TOPIC':
      console.log('📌 Topic cambiado:', data.channel);
      this.notifyEvent({
        type: 'TOPIC',
        channel: data.channel,
        text: data.text,
        timestamp: data.timestamp || Date.now(),
        metadata: data
      });
      break;

    case 'ERROR':
      console.error('❌ Error IRC:', data.text);
      this.notifyError({
        type: 'ERROR',
        text: data.text,
        timestamp: data.timestamp || Date.now(),
        metadata: data
      });
      break;

    case 'DISCONNECTED':
      console.log('🔌 Desconectado del IRC');
      this.notifyEvent({
        type: 'DISCONNECTED',
        text: data.text || 'Desconectado',
        timestamp: data.timestamp || Date.now(),
        metadata: data
      });
      break;

    default:
      console.warn('⚠️ Tipo de mensaje no manejado:', msgType);
      console.log('Data:', data);
  }
}

  private handleWebSocketClose(): void {
    console.log('⚠️ Conexión cerrada');
    this.stopHeartbeat();
    
    if (this.connected && this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`🔄 Intentando reconectar (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
      
      this.reconnectTimer = setTimeout(() => {
        if (this.currentConfig) {
          this.connect(this.currentConfig).catch(err => {
            console.error('Error en reconexión:', err);
          });
        }
      }, this.reconnectDelay * this.reconnectAttempts);
    }
  }

  async disconnect(): Promise<void> {
    try {
      console.log('🔌 Desconectando del IRC...');

      this.stopHeartbeat();
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }

      // Llamar a backend para desconectar
      await fetch(`${this.REST_API_URL}/linkhub/disconnect`, {
        method: 'POST',
        headers: this.getHeaders()
      });

      if (this.ws) {
        if ((this.ws as EventSource).close) {
          (this.ws as EventSource).close();
        } else if ((this.ws as WebSocket).close) {
          (this.ws as WebSocket).close();
        }
        this.ws = null;
      }

      this.connected = false;
      this.connectionId = null;
      this.currentConfig = null;

      console.log('✅ Desconectado correctamente');

    } catch (error) {
      console.error('Error al desconectar:', error);
      throw error;
    }
  }

  async sendMessage(target: string, message: string): Promise<void> {
    try {
      console.log(`📤 Enviando mensaje a ${target}: ${message}`);

      const response = await fetch(`${this.REST_API_URL}/linkhub/message`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ target, message })
      });

      if (!response.ok) {
        throw new Error(`Error enviando mensaje: ${response.status}`);
      }

      console.log('✅ Mensaje enviado');

    } catch (error) {
      console.error('Error enviando mensaje:', error);
      throw error;
    }
  }

  /**
   * Ejecutar comando IRC raw (WHOIS, MODE, etc.)
   * @param command - Comando IRC (ej: 'WHOIS', 'MODE', 'KICK')
   * @param args - Argumentos del comando (ej: ['Paula'], ['#canal', '+o', 'usuario'])
   */
  async executeCommand(command: string, args: string[]): Promise<void> {
    try {
      console.log(`🎮 Ejecutando comando IRC: ${command} ${args.join(' ')}`);

      const response = await fetch(`${this.REST_API_URL}/linkhub/command`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ command, args })
      });

      if (!response.ok) {
        throw new Error(`Error ejecutando comando: ${response.status}`);
      }

      console.log('✅ Comando IRC ejecutado');

    } catch (error) {
      console.error('Error ejecutando comando:', error);
      throw error;
    }
  }

  async joinChannel(channel: string): Promise<void> {
    try {
      if (!channel.startsWith('#')) {
        channel = `#${channel}`;
      }

      console.log(`➕ Uniéndose a ${channel}...`);

      const response = await fetch(`${this.REST_API_URL}/linkhub/join/${encodeURIComponent(channel)}`, {
        method: 'POST',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`Error uniéndose al canal: ${response.status}`);
      }

      console.log(`✅ Unido a ${channel}`);

    } catch (error) {
      console.error('Error uniéndose al canal:', error);
      throw error;
    }
  }

  async partChannel(channel: string): Promise<void> {
    try {
      if (!channel.startsWith('#')) {
        channel = `#${channel}`;
      }

      console.log(`➖ Saliendo de ${channel}...`);

      const response = await fetch(`${this.REST_API_URL}/linkhub/part/${encodeURIComponent(channel)}`, {
        method: 'POST',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`Error saliendo del canal: ${response.status}`);
      }

      console.log(`✅ Salió de ${channel}`);

    } catch (error) {
      console.error('Error saliendo del canal:', error);
      throw error;
    }
  }

  async getChannels(): Promise<IRCChannel[]> {
  try {
    // ✅ CAMBIO: Agregar userId como query param
    const url = `${this.REST_API_URL}/linkhub/channels?userId=${this.currentUserId || 'unknown'}`;
    
    const response = await fetch(url, {
      headers: this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Error obteniendo canales: ${response.status}`);
    }

    const data = await response.json();
    this.channelsCache = data.channels || [];
    console.log(`📺 Canales actualizados: ${this.channelsCache.length}`);
    
    return this.channelsCache;

  } catch (error) {
    console.error('Error obteniendo canales:', error);
    return [];
  }
}

  async getStats(): Promise<IRCStats | null> {
    try {
      const response = await fetch(`${this.REST_API_URL}/linkhub/stats`, {
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`Error obteniendo stats: ${response.status}`);
      }

      const data = await response.json();
      this.statsCache = data;
      console.log('📊 Estadísticas actualizadas');

      this.notifyStats(data);
      return data;

    } catch (error) {
      console.error('Error obteniendo estadísticas:', error);
      return null;
    }
  }

  onMessage(callback: (msg: IRCMessage) => void): () => void {
    this.messageListeners.add(callback);
    return () => this.messageListeners.delete(callback);
  }

  onEvent(callback: (event: IRCMessage) => void): () => void {
    this.eventListeners.add(callback);
    return () => this.eventListeners.delete(callback);
  }

  onError(callback: (error: IRCMessage) => void): () => void {
    this.errorListeners.add(callback);
    return () => this.errorListeners.delete(callback);
  }

  onStats(callback: (stats: IRCStats) => void): () => void {
    this.statsListeners.add(callback);
    return () => this.statsListeners.delete(callback);
  }

  private notifyMessage(message: IRCMessage): void {
    this.messageListeners.forEach(listener => {
      try {
        listener(message);
      } catch (error) {
        console.error('Error en listener de mensaje:', error);
      }
    });
  }

  private notifyEvent(event: IRCMessage): void {
    this.eventListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Error en listener de evento:', error);
      }
    });
  }

  private notifyError(error: IRCMessage): void {
    this.errorListeners.forEach(listener => {
      try {
        listener(error);
      } catch (error) {
        console.error('Error en listener de error:', error);
      }
    });
  }

  private notifyStats(stats: IRCStats): void {
    this.statsListeners.forEach(listener => {
      try {
        listener(stats);
      } catch (error) {
        console.error('Error en listener de stats:', error);
      }
    });
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && ((this.ws as any).readyState === EventSource.OPEN || (this.ws as any).readyState === WebSocket.OPEN)) {
        console.log('💓 Heartbeat - Conexión activa');
      }
    }, this.heartbeatTimeout);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  isConnected(): boolean {
    if (!this.ws) return false;
    
    if ((this.ws as any).readyState === EventSource.OPEN) {
      return this.connected;
    }
    
    if ((this.ws as any).readyState === WebSocket.OPEN) {
      return this.connected;
    }
    
    return false;
  }

  getConnectionStatus(): IRCConnectionStatus {
    return {
      connected: this.isConnected(),
      server: this.currentConfig?.server || '',
      nickname: this.currentConfig?.nickname || '',
      channels: this.currentConfig?.channels || [],
      error: this.connected ? undefined : 'Desconectado'
    };
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

const linkHubIrcService = new LinkHubIrcService();
export default linkHubIrcService;